import { useEffect } from "react";

/** Per-route SEO metadata manager (no dependency): sets document.title,
 * description, canonical, Open Graph/Twitter tags, robots and one JSON-LD
 * block; restores the default title on unmount. Google executes JS and
 * indexes these; true SSR is a separate infrastructure track. */

const SITE = "NaturePulse";

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function breadcrumbJsonLd(items) {
  // items: [{name, url(absolute)}]
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export default function SeoMeta({ title, description, canonical, ogImage, ogType = "website", jsonLd, noindex = false }) {
  const ldStr = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title.length > 60 ? title : `${title} · ${SITE}`;
    const fullTitle = document.title;
    setMeta("name", "description", description);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", canonical || window.location.href);
    setMeta("property", "og:image", ogImage);
    setMeta("name", "twitter:card", ogImage ? "summary_large_image" : "summary");
    setMeta("name", "robots", noindex ? "noindex,follow" : null);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    let ld = null;
    if (ldStr) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.setAttribute("data-seometa", "1");
      ld.textContent = ldStr;
      document.head.appendChild(ld);
    }
    return () => {
      document.title = prevTitle;
      if (ld) ld.remove();
    };
  }, [title, description, canonical, ogImage, ogType, ldStr, noindex]);
  return null;
}
